import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { SectionCard, SectionAction, EmptyState, Skeleton } from '@lighthouse/design'
import {
  IconCircleCheck, IconAlertTriangle, IconCircleX, IconCpu, IconClock, IconBell,
  IconFolder, IconPlugConnected, IconPlayerPlay, IconCircleDashed, IconAlertCircle,
} from '@tabler/icons-react'
import { useLiveOperations } from '../../hooks/useLiveOperations'
import { useApiHealth } from '../../hooks/useApiHealth'
import { useEtlRuns } from '../../hooks/useEtlRuns'
import { useSourceHealth, useWorkerFleet } from '../../hooks/useOverview'
import { useDeadLetterStats } from '../../hooks/useQueues'
import { SLOW } from '../../utils/polling'
import { HealthGroupIcon, HealthLineRow } from '../health/HealthLineRow'
import { apiCheckRows, deadLetterIntegrity, linkRowProps } from '../health/healthBoardFormat'
import { NOT_MEASURED, agoLabel, exactNumber, shortDuration } from '../../utils/format'
import type { EtlRunQuery } from '../../types/etlRuns'
import type { LiveOperation, LiveRun } from '../../types/liveOperations'
import {
  coverageBar, elapsedSeconds, elapsedText, livePhaseText, liveOperationsView,
  queryView, recentOutcome, recentWhen, requestErrorText, resumedText, runCountsLine, runStaleNote, sourcesLine,
  staleSinceText, workersLine,
} from './overviewFormat'
import type { OutcomeIcon } from './overviewFormat'

/* ---- Shared row mechanics ------------------------------------------------ */

function PanelError({ what, error }: { what: string; error: unknown }) {
  return (
    <EmptyState
      icon={<IconAlertCircle size={28} stroke={1.5} />}
      title={`Could not load ${what}`}
      message={requestErrorText(what, error)}
    />
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{
      padding: '8px 16px 2px', fontSize: 10, fontWeight: 600, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: 'var(--color-text-disabled)',
    }}
    >
      {children}
    </div>
  )
}

/* ---- Live Operations ----------------------------------------------------- */

/**
 * The elapsed readouts' clock: null until the first tick, then the browser time
 * every second — ONLY while `ticking`. An idle Observatory, or one showing only
 * stale rows, never re-renders once a second. Ages are measured on the server's
 * clock (overviewFormat.elapsedSeconds); this only advances them between polls.
 */
function useSecondClock(ticking: boolean): number | null {
  const [now, setNow] = useState<number | null>(null)
  useEffect(() => {
    if (!ticking) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [ticking])
  return now
}

/**
 * Entity names are single CamelCase words and run keys have no spaces, so neither can wrap. At phone
 * width the name column would push the timer off the card; truncate instead, with the full text
 * in the tooltip.
 */
const TRUNCATE = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const

export function RunLine({ run }: { run: LiveRun }) {
  const stale = runStaleNote(run)
  return (
    <div className="lh-op-row" data-run={run.executionId} data-stale={run.isStale || undefined}>
      <span className="lh-op-name">
        <span className="lh-op-dot" data-running={!run.isStale || undefined} data-waiting={run.isStale || undefined} />
        <span style={TRUNCATE} title={run.runKey}>{run.runKey}</span>
        {run.module && <span style={{ ...TRUNCATE, color: 'var(--color-text-disabled)', fontSize: 11 }}>{run.module}</span>}
      </span>
      <span className="lh-op-timer">started {agoLabel(run.startedAt)}</span>
      <span className="lh-op-meta">{runCountsLine(run)}</span>
      {stale && <span className="lh-op-meta" style={{ color: 'var(--status-warning-fg)' }}>{stale}</span>}
    </div>
  )
}

export function OperationRow({ op, elapsed }: { op: LiveOperation; elapsed: number | null }) {
  const stale = op.isHeartbeatStale
  const bar = coverageBar(op)
  // A resumed row's timer runs from its FIRST attempt, so the row says it was resumed.
  const meta = [op.module, livePhaseText(op), resumedText(op)].filter((part): part is string => Boolean(part)).join(' · ')

  return (
    <div className="lh-op-row" data-operation={op.entityRunId} data-stale={stale || undefined}>
      <span className="lh-op-name">
        <span className="lh-op-dot" data-running={!stale || undefined} data-waiting={stale || undefined} />
        <span style={TRUNCATE} title={op.entityName}>{op.entityName}</span>
      </span>
      {/* A stale row gets when it was last heard from, in warning tone, and no
          ticking timer: nothing on it is running. */}
      <span className="lh-op-timer" style={stale ? { color: 'var(--status-warning-fg)' } : undefined}>
        {stale ? staleSinceText(op.lastHeartbeatAt, op.startedAt) : elapsedText(elapsed)}
      </span>
      <span className="lh-op-meta">{meta}</span>
      {bar.kind !== 'none' && (
        <span className="lh-op-progress" title={bar.kind === 'indeterminate' ? bar.reason ?? undefined : undefined}>
          <span className="lh-op-track">
            <span
              className="lh-op-fill"
              data-indeterminate={bar.kind === 'indeterminate' || undefined}
              style={bar.kind === 'determinate' ? { width: `${bar.percent}%` } : undefined}
            />
          </span>
          <span className="lh-op-pct" style={{ whiteSpace: 'nowrap', minWidth: 0 }}>
            {bar.kind === 'determinate' ? bar.label : ''}
          </span>
        </span>
      )}
    </div>
  )
}

/**
 * What the worker has recorded as running — served by
 * `api/observatory/operations/live`, judged by the worker's own stale rule.
 *
 * Per run: row counts, never a percentage (a resume can push finished past the
 * total recorded at creation). Per entity: the newest chunk's live phase, and a
 * determinate bar only for a served share of the date WINDOW. Stale rows are
 * flagged, never dropped — nothing closes a dead worker's rows — but capped here
 * with the remainder counted, so they cannot bury the work actually in flight.
 */
export function LiveOperations() {
  const navigate = useNavigate()
  const q = useLiveOperations()
  const state = queryView(q)
  const view = state === 'ready' ? liveOperationsView(q.data!) : null
  const now = useSecondClock(Boolean(view?.ticking))

  return (
    <SectionCard
      title="Live Operations"
      flush
      action={<SectionAction onClick={() => navigate('/etl-activity')}>View timeline →</SectionAction>}
    >
      {state === 'error' ? (
        <PanelError what="live operations" error={q.error} />
      ) : state === 'loading' || !view || !q.data ? (
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={38} />)}
        </div>
      ) : view.isEmpty ? (
        <EmptyState title="Nothing running" message="No ETL work is recorded as in flight — live or stale." />
      ) : (
        <div style={{ paddingBottom: 8 }}>
          {view.liveRuns.length === 0 && view.liveOps.length === 0 && (
            <div style={{ padding: '10px 16px 0', fontSize: 12, color: 'var(--status-warning-fg)' }}>
              Nothing is live. Everything below is recorded as running with no heartbeat for more than{' '}
              {exactNumber(q.data.staleRule.minutes)} min.
            </div>
          )}

          {(view.liveRuns.length > 0 || view.staleRuns.length > 0) && (
            <>
              <SectionLabel>Runs</SectionLabel>
              {view.liveRuns.map((run) => <RunLine key={run.executionId} run={run} />)}
              {view.staleRuns.map((run) => <RunLine key={run.executionId} run={run} />)}
              {view.staleRunsHidden > 0 && (
                <div style={{ padding: '2px 16px 6px' }}>
                  <SectionAction onClick={() => navigate('/execution-history?status=stale')}>
                    {exactNumber(view.staleRunsHidden)} more stale run{view.staleRunsHidden === 1 ? '' : 's'} →
                  </SectionAction>
                </div>
              )}
            </>
          )}

          {(view.liveOps.length > 0 || view.staleOps.length > 0) && (
            <>
              <SectionLabel>Entity runs</SectionLabel>
              {view.liveOps.map((op) => (
                <OperationRow
                  key={op.entityRunId}
                  op={op}
                  elapsed={elapsedSeconds(op.startedAt, q.data!.observedAt, q.dataUpdatedAt, now)}
                />
              ))}
              {view.staleOps.map((op) => <OperationRow key={op.entityRunId} op={op} elapsed={null} />)}
              {view.liveOpsNotListed > 0 && (
                <div style={{ padding: '2px 16px 6px', fontSize: 11, color: 'var(--color-text-secondary)' }}>
                  {exactNumber(view.liveOpsNotListed)} more live entity run{view.liveOpsNotListed === 1 ? '' : 's'} not
                  listed — the server lists the newest 50.
                </div>
              )}
              {view.staleOpsHidden > 0 && (
                <div style={{ padding: '2px 16px 6px' }}>
                  <SectionAction onClick={() => navigate('/etl-explorer')}>
                    {exactNumber(view.staleOpsHidden)} more stale entity run{view.staleOpsHidden === 1 ? '' : 's'} →
                  </SectionAction>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </SectionCard>
  )
}

/* ---- System Health ------------------------------------------------------- */

function SkeletonRows({ count }: { count: number }) {
  return (
    <div style={{ padding: '6px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: count }, (_, i) => <Skeleton key={i} height={18} />)}
    </div>
  )
}

/** Things Observatory can name but nothing checks. A dash each, never green. */
const NOT_CHECKED: readonly { key: string; name: string; icon: ReactNode }[] = [
  { key: 'scheduler', name: 'Scheduler', icon: <IconClock size={15} /> },
  { key: 'notifications', name: 'Notification Service', icon: <IconBell size={15} /> },
  { key: 'storage', name: 'File Storage', icon: <IconFolder size={15} /> },
]

/**
 * System Health, composed from three served readings — and three it cannot make.
 *
 * - The API's own checks (`/api/health`), folded into recognisable rows, each
 *   taking its worst entry's status and showing what the check said. A failed
 *   REQUEST is an error row, never "Unhealthy": the API may be fine and merely
 *   unreachable from here. A known group the answer carries no entry for stays,
 *   as "Not checked by this instance" (healthBoardFormat.apiCheckRows), and the
 *   dead-letters row is checked against the per-queue reading — the rail badge's
 *   query, so no poll is added — because the check counts a queue it could not
 *   inspect as empty (healthBoardFormat.deadLetterIntegrity). The Health
 *   Monitoring page draws the same rows through the same two functions.
 * - Workers, from the worker projection; Sources, from the source projection —
 *   each a dash while it never reported or its projection is stale.
 * - Scheduler, Notification Service and File Storage: nothing checks them, so
 *   they say "Not checked" rather than borrowing a colour.
 *
 * "View health details" opens Health Monitoring (`/system-health`), the same
 * readings per host — through `navigate`, never an href, which inside the router
 * would be a full page reload. Workers and Sources rows open their own pages.
 */
export function SystemHealthPanel() {
  const navigate = useNavigate()
  const health = useApiHealth()
  const fleet = useWorkerFleet()
  const sources = useSourceHealth()
  const deadLetters = useDeadLetterStats({ refetchInterval: SLOW })

  const healthState = queryView(health)
  const fleetState = queryView(fleet)
  const sourceState = queryView(sources)
  const workers = fleetState === 'ready' ? workersLine(fleet.data!) : null
  const source = sourceState === 'ready' ? sourcesLine(sources.data!) : null

  return (
    <SectionCard
      title="System Health"
      subtitle="As the API, the worker and the source probes report it"
      flush
      action={<SectionAction onClick={() => navigate('/system-health')}>View health details →</SectionAction>}
    >
      {healthState === 'error' ? (
        <HealthLineRow
          rowKey="api-health"
          icon={<IconAlertCircle size={15} />}
          name="API health checks"
          detail={requestErrorText('the API health report', health.error)}
          status={null}
          statusLabel="no answer"
        />
      ) : healthState === 'loading' ? (
        <SkeletonRows count={3} />
      ) : Object.keys(health.data!.entries ?? {}).length === 0 ? (
        <HealthLineRow
          rowKey="api-health"
          icon={<IconAlertCircle size={15} />}
          name="API health checks"
          detail="The API reported no checks."
          status={null}
          statusLabel="no checks"
        />
      ) : (
        apiCheckRows(health.data!).map((checked) => {
          const row = deadLetterIntegrity(checked, deadLetters)
          const to = row.to
          return (
            <HealthLineRow
              key={row.key}
              rowKey={row.key}
              icon={<HealthGroupIcon groupKey={row.key} />}
              name={row.label}
              detail={row.detail}
              status={row.role}
              statusLabel={row.statusLabel}
              onOpen={to ? () => navigate(to) : undefined}
            />
          )
        })
      )}

      {fleetState === 'loading' ? (
        <SkeletonRows count={1} />
      ) : (
        <HealthLineRow
          rowKey="workers"
          icon={<IconCpu size={15} />}
          name="Workers"
          detail={workers ? workers.detail : requestErrorText('the worker fleet', fleet.error)}
          status={workers ? workers.status : null}
          statusLabel={workers ? workers.label : 'no answer'}
          onOpen={() => navigate('/workers')}
        />
      )}

      {sourceState === 'loading' ? (
        <SkeletonRows count={1} />
      ) : (
        <HealthLineRow
          rowKey="sources"
          icon={<IconPlugConnected size={15} />}
          name="Sources"
          detail={source ? source.detail : requestErrorText('source health', sources.error)}
          status={source ? source.status : null}
          statusLabel={source ? source.label : 'no answer'}
          onOpen={() => navigate('/sources')}
        />
      )}

      {NOT_CHECKED.map((row) => (
        <HealthLineRow key={row.key} rowKey={row.key} icon={row.icon} name={row.name} status={null} statusLabel="Not checked" />
      ))}
    </SectionCard>
  )
}

/* ---- Recent Executions --------------------------------------------------- */

/** One stable object, so the query key is one cache entry. */
const RECENT_QUERY: EtlRunQuery = { limit: 6 }

function outcomeIcon(icon: OutcomeIcon) {
  switch (icon) {
    case 'done': return <span style={{ color: 'var(--status-good)' }}><IconCircleCheck size={16} /></span>
    case 'warning': return <span style={{ color: 'var(--status-warning)' }}><IconAlertTriangle size={16} /></span>
    case 'failed': return <span style={{ color: 'var(--status-critical)' }}><IconCircleX size={16} /></span>
    case 'running': return <span style={{ color: 'var(--stage-active-track)' }}><IconPlayerPlay size={16} /></span>
    default: return <span style={{ color: 'var(--color-text-disabled)' }}><IconCircleDashed size={16} /></span>
  }
}

/**
 * The newest entity runs, from the Explorer's own endpoint — labelled the way
 * the Explorer labels them, because every row opens there. A stale run is never
 * the running icon, and a run with no measured duration shows a dash.
 */
export function RecentExecutions() {
  const navigate = useNavigate()
  const q = useEtlRuns(RECENT_QUERY)
  const state = queryView(q)

  return (
    <SectionCard
      title="Recent Executions"
      subtitle="Entity runs, newest first"
      flush
      action={<SectionAction onClick={() => navigate('/etl-explorer')}>View all →</SectionAction>}
    >
      {state === 'error' ? (
        <PanelError what="recent entity runs" error={q.error} />
      ) : state === 'loading' || !q.data ? (
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} height={30} />)}
        </div>
      ) : q.data.length === 0 ? (
        <EmptyState title="No entity runs recorded" message="Every ETL job is manual-trigger; run one from the Scheduler." />
      ) : (
        q.data.map((run) => {
          const outcome = recentOutcome(run.outcome)
          const open = () => navigate(`/etl-explorer/${encodeURIComponent(run.runId)}`)
          return (
            <div className="lh-exec-row" key={run.runId} data-run={run.runId} {...linkRowProps(open, `${run.entityName}: ${outcome.label}`)}>
              <span className="lh-exec-icon" title={outcome.label}>{outcomeIcon(outcome.icon)}</span>
              <span className="lh-exec-body">
                <span className="lh-exec-name">{run.entityName}</span>
                <span className="lh-exec-sub">{outcome.label} · {recentWhen(run)}</span>
              </span>
              <span className="lh-exec-duration">
                {run.totalDurationSeconds == null ? NOT_MEASURED : shortDuration(run.totalDurationSeconds)}
              </span>
            </div>
          )
        })
      )}
    </SectionCard>
  )
}
